/**
 * McpUserRow — una fila de la lista de acceso MCP: usuario, PATs activos y
 * el switch de habilitación.
 */
import { KeyRound, Loader2, ShieldCheck, ShieldOff, SlidersHorizontal, Check } from 'lucide-react';
import { useEffect, useState } from 'react';

const fmtWhen = (iso) => {
  if (!iso) return 'nunca';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

const LIMIT_FIELDS = [
  ['max_credits_per_run', 'por run'],
  ['max_credits_per_day', 'por día'],
  ['max_concurrent_runs', 'runs simultáneos'],
];

const MAX_NODES_RANGE = [10, 500];
const MAX_NODES_TOOLTIP = 'Cantidad máxima de nodos que el MCP (Claude u otro '
  + 'agente) puede tener en un proyecto. Protege contra agentes que creen '
  + 'nodos sin control.';

/** Campo inline, en el mismo renglón que el ON/OFF del MCP del usuario. */
const MaxNodesField = ({ user, busy, onSave }) => {
  const saved = user.limits?.max_nodes_per_project ?? 90;
  const [value, setValue] = useState(String(saved));
  const [error, setError] = useState(null);

  useEffect(() => { setValue(String(saved)); }, [saved]);

  const commit = () => {
    const number = Number(value);
    const [low, high] = MAX_NODES_RANGE;
    if (!Number.isInteger(number) || number < low || number > high) {
      setError(`Tiene que ser un número entre ${low} y ${high}`);
      setValue(String(saved));
      return;
    }
    setError(null);
    if (number !== saved) onSave({ max_nodes_per_project: number });
  };

  return (
    <label className="admin-mcp-maxnodes" title={MAX_NODES_TOOLTIP}>
      Máx. nodos por proyecto:
      <input
        type="number"
        min={MAX_NODES_RANGE[0]}
        max={MAX_NODES_RANGE[1]}
        value={value}
        disabled={busy}
        onChange={(e) => { setValue(e.target.value); setError(null); }}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        data-testid={`mcp-maxnodes-${user.user_id}`}
      />
      {busy && <Loader2 size={10} className="spin" />}
      {error && (
        <em className="admin-mcp-maxnodes-error" data-testid={`mcp-maxnodes-error-${user.user_id}`}>
          {error}
        </em>
      )}
    </label>
  );
};

const LimitsEditor = ({ user, saving, onSave }) => {
  const [draft, setDraft] = useState(user.limits || {});
  return (
    <div className="admin-mcp-limits" data-testid={`mcp-user-limits-${user.user_id}`}>
      {LIMIT_FIELDS.map(([key, label]) => (
        <label key={key}>
          {label}
          <input
            type="number"
            min="0"
            value={draft[key] ?? ''}
            onChange={(e) => setDraft({ ...draft, [key]: Number(e.target.value) })}
            data-testid={`mcp-limit-${key}-${user.user_id}`}
          />
        </label>
      ))}
      <button
        onClick={() => onSave(draft)}
        disabled={saving}
        data-testid={`mcp-limits-save-${user.user_id}`}
      >
        {saving ? <Loader2 size={11} className="spin" /> : <Check size={11} />} Guardar
      </button>
    </div>
  );
};

export const McpUserRow = ({ user, busy, onToggle, onSaveLimits }) => {
  const [openLimits, setOpenLimits] = useState(false);
  const plural = user.active_tokens === 1 ? '' : 's';
  return (
    <div
      className={`admin-mcp-user ${user.mcp_enabled ? 'enabled' : ''}`}
      data-testid={`mcp-user-row-${user.user_id}`}
    >
      <div className="admin-mcp-user-main">
        <span className="admin-mcp-user-name">
          {user.name || user.email || user.user_id}
          {user.role !== 'user' && (
            <em className={`admin-role-badge role-${user.role}`}>{user.role}</em>
          )}
        </span>
        <span className="admin-mcp-user-sub">
          {user.email || user.user_id}
          {user.not_in_page && ' · fuera de esta búsqueda'}
        </span>
      </div>

      <span
        className={`admin-mcp-tokens ${user.active_tokens ? 'has' : ''}`}
        title={`Último uso: ${fmtWhen(user.last_token_use)}`}
        data-testid={`mcp-user-tokens-${user.user_id}`}
      >
        <KeyRound size={11} />
        {user.active_tokens} PAT{plural} activo{plural}
      </span>

      <button
        className={`admin-btn ${user.mcp_enabled ? 'admin-btn-deduct' : 'admin-btn-grant'}`}
        onClick={() => onToggle(user)}
        disabled={busy}
        data-testid={`mcp-user-toggle-${user.user_id}`}
      >
        {busy
          ? <Loader2 size={12} className="spin" />
          : user.mcp_enabled ? <ShieldOff size={12} /> : <ShieldCheck size={12} />}
        {user.mcp_enabled ? 'Quitar MCP' : 'Habilitar MCP'}
      </button>

      <MaxNodesField
        user={user}
        busy={busy}
        onSave={(limits) => onSaveLimits(user, limits)}
      />

      <button
        className="admin-mcp-limits-toggle"
        onClick={() => setOpenLimits((v) => !v)}
        title="Presupuesto MCP de este usuario"
        data-testid={`mcp-limits-toggle-${user.user_id}`}
      >
        <SlidersHorizontal size={11} />
        {user.limits.max_credits_per_run}/run · {user.limits.max_credits_per_day}/día
        · {user.limits.max_concurrent_runs} run{user.limits.max_concurrent_runs === 1 ? '' : 's'}
      </button>

      {openLimits && (
        <LimitsEditor
          user={user}
          saving={busy}
          onSave={(limits) => onSaveLimits(user, limits)}
        />
      )}
    </div>
  );
};

export default McpUserRow;
